import React, { useState, useEffect, useContext, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { translate } from "../../utils/Translation";
import "./Page.css";
import "./ActionsReviews.css";
import ActionsReviewsSections from "./ActionsReviewsSections";

const ActionsReviews = () => {
  const auth = useAuth();
  const { hId, lang } = useContext(HotelContext);

  const [ratingDistribution, setRatingDistribution] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const maxRatingCount = Math.max(...(ratingDistribution || []).map((d) => d.count || 0), 1);

  const formatDate = useCallback(
    (dateString) => {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return translate("v2_reviews_today", lang);
      if (diffDays === 1) return translate("v2_reviews_one_day_ago", lang);
      return translate("v2_reviews_days_ago", lang, { days: diffDays });
    },
    [lang]
  );

  const getPlatformColorByPosition = (position) => {
    const colors = ["#3C5544", "#96B77A", "#F4EDDE", "#B8C5A0", "#D9D4C7", "#A8B896"];
    return colors[position - 1] || colors[0];
  };

  useEffect(() => {
    const fetchReviewsStats = async () => {
      if (!auth.isAuthenticated || !auth.user?.access_token || !hId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);

        const statsResponse = await fetch(`/api/hotels/${hId}/reviews/stats`, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + auth.user.access_token,
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-front-token": "Bs#PSQyhy)!y9Ti{",
          },
        });

        if (!statsResponse.ok) {
          const errorData = await statsResponse.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${statsResponse.status}`);
        }

        const statsData = await statsResponse.json();

        if (statsData.success === "OK" && statsData.stats) {
          setRatingDistribution(statsData.stats.ratingDistribution || []);

          const sortedPlatforms = [...(statsData.stats.platformData || [])].sort((a, b) => b.count - a.count);
          const formattedPlatforms = sortedPlatforms.map((platform, index) => {
            const position = index + 1;
            const color = getPlatformColorByPosition(position);
            const normalizedName = platform.platform.toLowerCase();
            let className = "other";
            if (normalizedName.includes("google")) {
              className = "google-maps";
            } else if (normalizedName.includes("booking")) {
              className = "booking";
            } else if (normalizedName.includes("tripadvisor")) {
              className = "tripadvisor";
            }
            return {
              name: platform.platform,
              count: platform.count,
              percentage: platform.percentage,
              color,
              className,
            };
          });
          setPlatformData(formattedPlatforms);

          setAverageRating(statsData.stats.averageRating || 0);
          setTotalReviews(statsData.stats.totalReviews || 0);
        }

        const reviewsResponse = await fetch(`/api/hotels/${hId}/reviews`, {
          method: "GET",
          headers: {
            Authorization: "Bearer " + auth.user.access_token,
            Accept: "application/json",
            "Content-Type": "application/json",
            "x-front-token": "Bs#PSQyhy)!y9Ti{",
          },
        });

        if (!reviewsResponse.ok) {
          const errorData = await reviewsResponse.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${reviewsResponse.status}`);
        }

        const reviewsData = await reviewsResponse.json();

        if (reviewsData.success === "OK" && reviewsData.reviews) {
          const formattedReviews = reviewsData.reviews.map((review) => ({
            id: review.id,
            name: review.author_name,
            rating: review.rating,
            date: formatDate(review.created_at),
            text: review.review_text,
            platform: review.platform,
          }));
          setReviews(formattedReviews);
        } else {
          setReviews([]);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching reviews:", err);
        setError(true);
        setLoading(false);
      }
    };

    if (auth.isAuthenticated && hId) {
      fetchReviewsStats();
    }
  }, [auth.isAuthenticated, auth.user?.access_token, hId, formatDate]);

  const renderStars = (count) => {
    const rating = Math.round(count || 0);
    return Array.from({ length: 5 }, (_, i) => (
      <span
        key={i}
        className="actions-reviews__star"
        style={{ color: i < rating ? "#3C5544" : "#e0e0e0" }}
      >
        ★
      </span>
    ));
  };

  return (
    <div className="v2-page">
      <div className="v2-page__header">
        <h1>{translate("v2_actions_reviews_title", lang)}</h1>
      </div>
      <div className="v2-page__content">
        <ActionsReviewsSections
          loading={loading}
          error={error}
          ratingDistribution={ratingDistribution}
          platformData={platformData}
          reviews={reviews}
          averageRating={averageRating}
          totalReviews={totalReviews}
          maxRatingCount={maxRatingCount}
          renderStars={renderStars}
        />
      </div>
    </div>
  );
};

export default ActionsReviews;
